<script setup lang="ts">
import { ref, PropType, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Participant } from "../../../types/PersologOnlineTypes.ts";
import { isValidEmail } from "../../../utils/GeneralUtils.ts";
import ParticipantExcelInstructions from "./ParticipantExcelInstructions.vue";

const { t } = useI18n();

const props = defineProps({
    existingParticipants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
});

const emit = defineEmits(["update:valid", "update:participants"]);

const participantValue = ref<string>();
const existingParticipants = ref<Participant[] | undefined>(props.existingParticipants);
const participantInvalid = ref<boolean>(false);

const collapsed = ref<boolean>(true);

function splitInput(input: string): string[] {
    const regex = /(?:"([^"]*)"|([^;,"\s]+))(?=\s*[;,\s]|$)/g;
    const parts = [];

    let match;
    while ((match = regex.exec(input)) !== null) {
        parts.push(match[1] ?? match[2]);
    }

    return parts;
}

/**
 * "Creates" a participant from a line of text for internal component use.
 * @param line The line to create a participant from.
 */
function createEmptyParticipant(line: string): Participant | null {
    const parts = splitInput(line);

    if (parts.length === 0) return null;
    if (!isValidEmail(parts[0])) return null;

    return {
        email: parts[0],
        firstName: parts[1] || "",
        name: parts.slice(2).join(" ") || "",
        projectId: 0,
        selectedProduct: "",
    };
}

/**
 * Parses the participants from the textarea and updates the participants ref.
 * Participant marked as "DELETED" are not updated to the participants ref.
 * Only new participants are added to the participants ref.
 */
function parseParticipants() {
    const lines = participantValue.value ? participantValue.value?.trim().split("\n") : null;
    const participantMap = new Map<string, Participant>();

    participantInvalid.value = false;

    if (lines) {
        lines.every((line: string) => {
            const participant = createEmptyParticipant(line);
            if (participant) {
                if (!existingParticipants.value?.some((p) => p.email === participant.email)) {
                    // participant does not exist yet; no duplication
                    participantMap.set(participant.email, participant);
                }
                return true;
            } else {
                participantInvalid.value = true;
                return false;
            }
        });
    }

    const updatedParticipants = [...participantMap.values()];
    emit("update:participants", updatedParticipants);
    emit("update:valid", !participantInvalid.value);
}

watch(
    () => props.existingParticipants,
    (newVal) => {
        existingParticipants.value = newVal;
    },
    { immediate: true },
);

</script>

<template>
    <div class="flex flex-wrap gap-4 mt-4">
        <Panel id="participant-excel-panel" toggleable v-model:collapsed="collapsed" class="w-full">
            <template #header>
                <div class="w-full cursor-pointer" @click="collapsed = !collapsed">
                    <strong>{{ t("project-configuration.pasteFromExcel.heading") }}</strong>
                </div>
            </template>
            <div class="mb-4 flex-1">
                <p>{{ t("project-configuration.pasteFromExcel.instructions.short") }}</p>
                <ParticipantExcelInstructions />
            </div>
            <div class="flex flex-wrap mb-4 flex-1 mt-8 gap-4">
                <div class="basis-11/12">
                    <IconField>
                        <label for="participants">{{ t("project-configuration.participants") }}</label>
                        <Textarea
                            id="participants"
                            placeholder="test@example.com, John, Doe"
                            rows="5"
                            cols="60"
                            auto-resize
                            v-model="participantValue"
                            @focusout="parseParticipants"
                            @keyup.enter="parseParticipants"
                            :class="['w-full', { 'p-invalid': participantInvalid }]"
                            autocomplete="off"
                            autocapitalize="off"
                        />
                        <InputIcon class="pi pi-users" />
                    </IconField>
                </div>
                <div class="flex-1 flex justify-end items-start">
                    <Button icon="pi pi-plus" @click="parseParticipants" :aria-label="t('accessibility.buttons.parseParticipants')" />
                </div>
            </div>
        </Panel>
    </div>
</template>
